import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Title,
  Text,
  SimpleGrid,
  Card,
  Group,
  ThemeIcon,
  Stack,
  Badge,
  Box,
  Paper,
  RingProgress,
  SegmentedControl,
  Anchor,
  useMantineTheme,
  Tooltip,
  Alert,
  Button,
} from '@mantine/core';
import {
  IconClipboardText,
  IconTruck,
  IconArrowRight,
  IconCheck,
  IconClock,
  IconAlertTriangle,
  IconPlayerPlay,
  IconCar,
  IconMapPin,
  IconUser,
  IconCalendar,
  IconAlertCircle,
  IconRoute,
} from '@tabler/icons-react';
import { useAuthStore } from '../stores/auth';
import { startGuidedTour, hasCompletedGuidedTour } from '../components/onboarding/GuidedTour';
import { ServiceReportStatusSetupBanner } from '../components/ServiceReportStatusSetupBanner';
import { CalendarOutlookSetupBanner } from '../components/CalendarOutlookSetupBanner';
import { DashboardActionCards } from './dashboard/DashboardActionCards';
import { DailyBriefingPanel } from '../components/DailyBriefingPanel';
import { PersonalBriefingPanel } from '../components/PersonalBriefingPanel';
import { ReadyDeliveriesWidget } from './dashboard/ReadyDeliveriesWidget';
import { UpcomingCalendarWidget } from './dashboard/UpcomingCalendarWidget';
import { TimeProposalsWidget } from './dashboard/TimeProposalsWidget';
import { AttendanceWidget } from './dashboard/AttendanceWidget';
import { formatElapsedTime, isOverdue } from './dashboard/dashboardUtils';
import { useDashboardData, PERIOD_LABELS, type StatsPeriod } from './dashboard/useDashboardData';
import styles from './DashboardPage.module.css';

export function DashboardPage() {
  const navigate = useNavigate();
  const theme = useMantineTheme();
  const { user } = useAuthStore();
  const { submissions, stats, parkedSubmissions, successRate, period, setPeriod } = useDashboardData();

  // Einmaliger Hinweis auf den Rundgang. Bewusst kein Modal, das sich beim Login
  // vor die Arbeit schiebt — wer nichts davon wissen will, klickt einmal weg.
  const [tourDismissed, setTourDismissed] = useState(
    () => hasCompletedGuidedTour() || localStorage.getItem('servicehub.tour.hintHidden') === '1',
  );

  const dismissTourHint = () => {
    try {
      localStorage.setItem('servicehub.tour.hintHidden', '1');
    } catch {
      // Ohne localStorage erscheint der Hinweis eben wieder — kein Grund zu scheitern.
    }
    setTourDismissed(true);
  };

  return (
    <Container size="xl" py="xl">
      <Stack gap="xl">
        {/* Einrichtungslücke, die Technikern erst mitten im Absenden auffällt und die
            nur Admins beheben können — deshalb hier, vorher. */}
        <ServiceReportStatusSetupBanner />
        {/* Solange die Firmen-Berechtigung fehlt, kommen Team-Termine nicht bei allen in Outlook an. */}
        <CalendarOutlookSetupBanner />

        {!tourDismissed && (
          <Alert
            color="orange"
            variant="light"
            icon={<IconRoute size={18} />}
            withCloseButton
            onClose={dismissTourHint}
            title="Neu hier? Eine Viertelstunde, einmal durchs Portal"
          >
            <Text size="sm" mb="xs">
              Der geführte Rundgang zeigt dir auf der echten Oberfläche, wofür welcher Bereich
              da ist — und welche Funktionen du gar nicht brauchst.
            </Text>
            <Button size="xs" onClick={startGuidedTour} leftSection={<IconPlayerPlay size={14} />}>
              Rundgang starten
            </Button>
          </Alert>
        )}

        {/* Stempeln und wer gerade arbeitet (Anwesenheit, v1.405.0). */}
        <AttendanceWidget />
        {/* Zeitvorschläge von Kollegen, die auf deine Freigabe warten — ohne keine Karte. */}
        <TimeProposalsWidget />
        {/* Persönliches Briefing zuerst — was heute für dich anliegt. Team-Lage darunter. */}
        <PersonalBriefingPanel compact showTechnicianSelect={false} />
        {/* Termine der nächsten Tage — auch Team-Termine; ohne Termine keine Karte. */}
        <UpcomingCalendarWidget />
        <DailyBriefingPanel compact />

        {/* Welcome Section */}
        <Box>
          <Text size="sm" c="dimmed" tt="uppercase" fw={500}>
            Willkommen zurück
          </Text>
          <Title order={1} className={styles.title}>
            Hallo, {user?.displayName?.split(' ')[0] ?? 'dort'} 👋
          </Title>
          <Text c="dimmed" size="lg" mt="xs">
            Was möchtest du heute dokumentieren?
          </Text>
        </Box>

        {/* Stats Overview */}
        <Box>
        <Group justify="space-between" mb="md" align="center">
          <Group gap="xs" align="center">
            <Text size="sm" c="dimmed" tt="uppercase" fw={500}>
              Statistik
            </Text>
            <Anchor size="xs" onClick={() => navigate('/statistik')}>
              Alle Metriken →
            </Anchor>
          </Group>
          <SegmentedControl
            size="xs"
            value={period}
            onChange={(val) => setPeriod(val as StatsPeriod)}
            data={Object.entries(PERIOD_LABELS).map(([value, label]) => ({ value, label }))}
          />
        </Group>
        <SimpleGrid cols={{ base: 1, sm: 2, md: 4 }} spacing="md">
          <Paper className={styles.statCard} p="md">
            <Group justify="space-between" align="flex-start">
              <div>
                <Text size="xs" c="dimmed" tt="uppercase" fw={500}>
                  Erfolgreich
                </Text>
                <Text size="xl" fw={700} className={styles.statNumber}>
                  {stats.success}
                </Text>
              </div>
              <ThemeIcon size="lg" radius="md" color="green" variant="light">
                <IconCheck size={20} />
              </ThemeIcon>
            </Group>
          </Paper>

          <Paper className={styles.statCard} p="md">
            <Group justify="space-between" align="flex-start">
              <div>
                <Text size="xs" c="dimmed" tt="uppercase" fw={500}>
                  In Bearbeitung
                </Text>
                <Text size="xl" fw={700} className={styles.statNumber}>
                  {stats.pending}
                </Text>
              </div>
              <ThemeIcon size="lg" radius="md" color="yellow" variant="light">
                <IconClock size={20} />
              </ThemeIcon>
            </Group>
          </Paper>

          <Paper className={styles.statCard} p="md">
            <Group justify="space-between" align="flex-start">
              <div>
                <Text size="xs" c="dimmed" tt="uppercase" fw={500}>
                  Fehlgeschlagen
                </Text>
                <Text size="xl" fw={700} className={styles.statNumber}>
                  {stats.failed}
                </Text>
              </div>
              <ThemeIcon size="lg" radius="md" color="red" variant="light">
                <IconAlertTriangle size={20} />
              </ThemeIcon>
            </Group>
          </Paper>

          <Paper className={styles.statCard} p="md">
            <Group justify="space-between" align="flex-start">
              <div>
                <Text size="xs" c="dimmed" tt="uppercase" fw={500}>
                  Erfolgsquote
                </Text>
                <Text size="xl" fw={700} className={styles.statNumber}>
                  {stats.success === 0 && stats.failed === 0 ? '–' : `${successRate}%`}
                </Text>
              </div>
              <RingProgress
                size={44}
                thickness={4}
                roundCaps
                sections={[
                  {
                    value: stats.success === 0 && stats.failed === 0 ? 0 : successRate,
                    color: theme.colors.cyberOrange?.[6] ?? '#ff6600',
                  },
                ]}
              />
            </Group>
          </Paper>
        </SimpleGrid>
        </Box>

        {/* In Bearbeitung (Geparkte Aufträge) - Prominent anzeigen */}
        {parkedSubmissions.length > 0 && (
          <Box>
            <Group justify="space-between" mb="md">
              <Group gap="xs">
                <ThemeIcon size="sm" color="blue" variant="light" radius="xl">
                  <IconPlayerPlay size={14} />
                </ThemeIcon>
                <Text size="sm" c="dimmed" tt="uppercase" fw={500}>
                  In Bearbeitung ({parkedSubmissions.length})
                </Text>
                {parkedSubmissions.length > 1 && (
                  <Tooltip label="Mehrere Aufträge gleichzeitig in Bearbeitung!">
                    <ThemeIcon size="sm" color="red" variant="filled" radius="xl">
                      <IconAlertCircle size={12} />
                    </ThemeIcon>
                  </Tooltip>
                )}
              </Group>
            </Group>
            <SimpleGrid cols={{ base: 1, md: 2 }} spacing="md">
              {parkedSubmissions.map((submission) => {
                const startTime = submission.workStartAt
                  ? new Date(submission.workStartAt)
                  : submission.parkedAt
                    ? new Date(submission.parkedAt)
                    : new Date(submission.createdAt);
                const overdue = isOverdue(startTime);
                const elapsedTime = formatElapsedTime(startTime);
                
                // Kundenname aus verschiedenen Quellen
                const customerName = submission.cwCompanyName 
                  || (submission.ticketSummary?.split(' - ')[0])
                  || null;
                
                return (
                  <Card
                    key={submission.id}
                    className={`${styles.parkedCard} ${overdue ? styles.parkedCardOverdue : ''}`}
                    onClick={() => navigate(`/${submission.formType === 'ONSITE_SERVICE' ? 'service-report' : 'delivery-report'}/${submission.id}`)}
                    padding="lg"
                    withBorder
                    style={overdue ? { borderColor: 'var(--mantine-color-red-6)' } : undefined}
                  >
                    <Group justify="space-between" mb="sm">
                      <Group gap="sm">
                        <ThemeIcon
                          size="lg"
                          radius="md"
                          color={overdue ? 'red' : 'blue'}
                          variant="light"
                        >
                          {submission.formType === 'ONSITE_SERVICE' ? (
                            <IconClipboardText size={18} />
                          ) : (
                            <IconTruck size={18} />
                          )}
                        </ThemeIcon>
                        <div>
                          <Text size="sm" fw={600}>
                            {submission.formType === 'ONSITE_SERVICE'
                              ? 'Service Report'
                              : 'Delivery Report'}
                            {submission.cwTicketId && ` #${submission.cwTicketId}`}
                          </Text>
                          {customerName && (
                            <Group gap={4}>
                              <IconUser size={12} color="var(--mantine-color-dimmed)" />
                              <Text size="xs" c="dimmed" fw={500}>
                                {customerName}
                              </Text>
                            </Group>
                          )}
                        </div>
                      </Group>
                      <Stack gap={4} align="flex-end">
                        <Badge color={overdue ? 'red' : 'blue'} variant="light">
                          {overdue ? '⚠️ ÜBERFÄLLIG' : 'GEPARKT'}
                        </Badge>
                      </Stack>
                    </Group>
                    
                    {/* Datum und laufende Zeit */}
                    <Paper p="xs" radius="sm" bg={overdue ? 'red.9' : 'dark.6'} mb="sm">
                      <Group justify="space-between">
                        <Group gap="xs">
                          <IconCalendar size={14} color="var(--mantine-color-dimmed)" />
                          <Text size="xs" c="dimmed">
                            {startTime.toLocaleDateString('de-DE', {
                              weekday: 'short',
                              day: '2-digit',
                              month: '2-digit',
                              year: 'numeric',
                            })}
                          </Text>
                        </Group>
                        <Tooltip label={`Gestartet um ${startTime.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' })}`}>
                          <Badge 
                            color={overdue ? 'red' : 'cyan'} 
                            variant="filled" 
                            size="lg"
                            leftSection={<IconClock size={12} />}
                          >
                            {elapsedTime}
                          </Badge>
                        </Tooltip>
                      </Group>
                    </Paper>
                    
                    <Group gap="lg">
                      {submission.departureAddress && (
                        <Group gap="xs">
                          <IconMapPin size={14} color="var(--mantine-color-dimmed)" />
                          <Text size="xs" c="dimmed">
                            Von: {submission.departureAddress?.substring(0, 20)}...
                          </Text>
                        </Group>
                      )}
                      {submission.travelKmOut && (
                        <Group gap="xs">
                          <IconCar size={14} color="var(--mantine-color-dimmed)" />
                          <Text size="xs" c="dimmed">
                            {submission.travelKmOut} km
                          </Text>
                        </Group>
                      )}
                    </Group>
                    
                    <Group justify="flex-end" mt="md">
                      <Badge 
                        color="cyberOrange" 
                        variant="filled" 
                        rightSection={<IconArrowRight size={12} />}
                        style={{ cursor: 'pointer' }}
                      >
                        Fortsetzen
                      </Badge>
                    </Group>
                  </Card>
                );
              })}
            </SimpleGrid>
          </Box>
        )}

        {/* Lieferbereit (Pipeline READY) */}
        <ReadyDeliveriesWidget />

        {/* Action Cards */}
        <DashboardActionCards
          navigate={navigate}
          theme={theme}
          draftsCount={stats.drafts}
        />

        {/* Recent Activity */}
        {submissions.length > 0 && (
          <Box>
            <Group justify="space-between" mb="md">
              <Text size="sm" c="dimmed" tt="uppercase" fw={500}>
                Letzte Aktivitäten
              </Text>
              <Text
                size="sm"
                c="cyberOrange"
                style={{ cursor: 'pointer' }}
                onClick={() => navigate('/submissions')}
              >
                Alle anzeigen →
              </Text>
            </Group>
            <Stack gap="sm">
              {submissions.slice(0, 5).map((submission) => (
                <Paper
                  key={submission.id}
                  className={styles.activityCard}
                  p="md"
                  onClick={() => navigate(`/${submission.formType === 'ONSITE_SERVICE' ? 'service-report' : 'delivery-report'}/${submission.id}`)}
                >
                  <Group justify="space-between">
                    <Group gap="md">
                      <ThemeIcon
                        size="lg"
                        radius="md"
                        variant="light"
                        color={submission.formType === 'ONSITE_SERVICE' ? 'orange' : 'cyan'}
                      >
                        {submission.formType === 'ONSITE_SERVICE' ? (
                          <IconClipboardText size={18} />
                        ) : (
                          <IconTruck size={18} />
                        )}
                      </ThemeIcon>
                      <div>
                        <Text size="sm" fw={500}>
                          {submission.formType === 'ONSITE_SERVICE'
                            ? 'Service Report'
                            : 'Delivery Report'}
                          {submission.cwTicketId && ` #${submission.cwTicketId}`}
                        </Text>
                        <Text size="xs" c="dimmed">
                          {new Date(submission.createdAt).toLocaleDateString('de-DE', {
                            day: '2-digit',
                            month: '2-digit',
                            year: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit',
                          })}
                        </Text>
                      </div>
                    </Group>
                    <Badge
                      variant="light"
                      color={
                        submission.status === 'CW_SUCCESS'
                          ? 'green'
                          : submission.status === 'CW_FAILED'
                          ? 'red'
                          : submission.status === 'DRAFT'
                          ? 'gray'
                          : submission.status === 'PARKED'
                          ? 'blue'
                          : 'yellow'
                      }
                    >
                      {submission.status === 'CW_SUCCESS'
                        ? 'Erfolgreich'
                        : submission.status === 'CW_FAILED'
                        ? 'Fehlgeschlagen'
                        : submission.status === 'DRAFT'
                        ? 'Entwurf'
                        : submission.status === 'PARKED'
                        ? 'Geparkt'
                        : 'In Bearbeitung'}
                    </Badge>
                  </Group>
                </Paper>
              ))}
            </Stack>
          </Box>
        )}
      </Stack>
    </Container>
  );
}

